/**
 * Dettaglio turno cassa con chiusura e quadratura.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@floraltech/ui';
import { ArrowLeft } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

interface TurnoInfo {
  id: string;
  stato: string;
  apertura_at: string;
  chiusura_at: string | null;
  fondo_cassa_iniziale: string;
  fondo_cassa_finale: string | null;
  totale_contanti: string;
  totale_elettronico: string;
  totale_assegni: string;
  totale_altro: string;
  scostamento: string | null;
  note_scostamento: string | null;
  note: string | null;
}

const TurnoDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fmt = useFormatters();

  const [turno, setTurno] = useState<TurnoInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [fondoFinale, setFondoFinale] = useState('');
  const [noteScostamento, setNoteScostamento] = useState('');
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const carica = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/cassa/turni/${id}`, { headers: authHeaders() });
      if (res.ok) setTurno(await res.json());
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const chiudiTurno = async () => {
    if (!id || !turno) return;
    setClosing(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/cassa/turni/${id}/chiudi`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          fondo_cassa_finale: fondoFinale,
          note_scostamento: noteScostamento || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore chiusura turno');
    } finally {
      setClosing(false);
    }
  };

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  const formatCurrency = (value: string): string => fmt.currency(value);

  if (loading) {
    return <div className="p-6"><p className="text-sm text-muted-foreground">Caricamento…</p></div>;
  }

  if (!turno) {
    return <div className="p-6"><p className="text-sm text-destructive">Turno non trovato</p></div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Turno cassa
        </h1>
        <Badge variant={turno.stato === 'aperto' ? 'outline' : 'secondary'}>
          {turno.stato}
        </Badge>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Dettagli turno */}
      <Card>
        <CardHeader>
          <CardTitle>Riepilogo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Apertura</p>
              <p className="font-mono">{formatDateTime(turno.apertura_at)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Chiusura</p>
              <p className="font-mono">{turno.chiusura_at ? formatDateTime(turno.chiusura_at) : '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Fondo iniziale</p>
              <p className="font-mono">{formatCurrency(turno.fondo_cassa_iniziale)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Fondo finale</p>
              <p className="font-mono">{turno.fondo_cassa_finale ? formatCurrency(turno.fondo_cassa_finale) : '—'}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Contanti</p>
              <p className="font-mono text-lg">{formatCurrency(turno.totale_contanti)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Elettronico</p>
              <p className="font-mono text-lg">{formatCurrency(turno.totale_elettronico)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Assegni</p>
              <p className="font-mono text-lg">{formatCurrency(turno.totale_assegni)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Altro</p>
              <p className="font-mono text-lg">{formatCurrency(turno.totale_altro)}</p>
            </div>
          </div>
          {turno.scostamento != null && (
            <div className="mt-4 border-t border-border pt-4">
              <p className="text-sm text-muted-foreground">Scostamento quadratura</p>
              <p
                className={`font-mono text-lg ${
                  parseFloat(turno.scostamento) !== 0 ? 'text-destructive' : ''
                }`}
              >
                {formatCurrency(turno.scostamento)}
              </p>
              {turno.note_scostamento && (
                <p className="mt-1 text-sm">{turno.note_scostamento}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chiusura turno */}
      {turno.stato === 'aperto' && (
        <Card>
          <CardHeader>
            <CardTitle>Chiudi turno</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-muted-foreground">
                  Fondo cassa contato
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={fondoFinale}
                  onChange={(e) => setFondoFinale(e.target.value)}
                  className="w-40"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-muted-foreground">
                  Note scostamento (obbligatorie se c'è differenza)
                </label>
                <textarea
                  className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  value={noteScostamento}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNoteScostamento(e.target.value)}
                  rows={2}
                />
              </div>
              <Button onClick={chiudiTurno} disabled={closing || !fondoFinale}>
                Chiudi turno
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TurnoDetail;
